import {
    Component,
    Signal,
    signal,
    computed,
    ChangeDetectorRef,
    OnInit,
    OnDestroy,
} from '@angular/core'
import { SharedModule } from '@app/shared/shared.module'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { AnalyticsService } from '@app/core/_services/analytics.service'
import { LocalStorageAccessService } from '@app/core/_services/local-storage-access.service'
import { ViewportService } from '@app/core/_services/viewport.service'
import { UserService } from '@app/core/_services/user.service'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { CommonModule } from '@angular/common'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { Subject, takeUntil } from 'rxjs'

@Component({
    imports: [SharedModule, CommonModule, FontAwesomeModule],
    selector: 'fm-year-in-focus-sidebar',
    templateUrl: './year-in-focus-sidebar.component.html',
    styleUrl: './year-in-focus-sidebar.component.scss',
    host: { class: 'contents' },
})
export class YearInFocusSidebarComponent implements OnInit, OnDestroy {
    faTimes = faTimes

    public isWelcomeChecklistActive: Signal<boolean> =
        this.welcomeChecklistService.isWelcomeChecklistActive

    public isDismissed = signal<boolean>(false)
    public hasYearlyReview = signal<boolean>(false)

    private ngUnsubscribe: Subject<any> = new Subject<any>()

    public shouldShow: Signal<boolean> = computed(() => {
        // Only show if user has yearlyReview data
        if (!this.hasYearlyReview()) {
            return false
        }
        // On desktop, always show if welcome checklist is not active (no dismissal)
        // On mobile, check dismissal status
        if (this.viewportService.isMobile()) {
            return !this.isWelcomeChecklistActive() && !this.isDismissed()
        } else {
            return !this.isWelcomeChecklistActive()
        }
    })

    constructor(
        private welcomeChecklistService: WelcomeChecklistService,
        private dialogService: DialogService,
        private analyticsService: AnalyticsService,
        private localStorageService: LocalStorageAccessService,
        private cdr: ChangeDetectorRef,
        public viewportService: ViewportService,
        private userService: UserService,
    ) {
        // Check if dismissed from localStorage
        const dismissed = this.localStorageService.getItem('yearInFocusSidebarDismissed')
        this.isDismissed.set(dismissed === 'true')
    }

    ngOnInit() {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            this.hasYearlyReview.set(!!user?.yearlyReview)
            this.cdr.markForCheck()
        })
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }

    onClickYearInFocus() {
        this.analyticsService.logClickedEvent('Year in Focus', 'Calendar')
        this.dialogService.openYearInReviewModal()
    }

    onDismiss(event: Event) {
        event.stopPropagation()
        this.analyticsService.logClickedEvent('Dismiss Year in Focus', 'Calendar')
        this.isDismissed.set(true)
        this.localStorageService.setItem(
            'yearInFocusSidebarDismissed',
            'true',
            'YearInFocusSidebar',
        )
        this.cdr.markForCheck()
    }
}
